import { Loader2 } from 'lucide-react';
import { BoxOverlay } from './BoxOverlay';
import { CropTile } from './CropStrip';
import { cropRings, type ListingCrop } from './listingCropsRun';

// ONE PART, AGAINST EVERY PHOTOGRAPH — all of them, in the same order, in every cell of
// the row, with the part ringed on the ones it was found in and nothing drawn on the rest.
//
// The whole picture rather than the rectangle cut out of it. A crop of a chair leg at
// thumbnail size is a brown smear — every one of them is, and two parts found in the same
// photograph gave two brown smears side by side. What the row is actually saying is WHERE
// the part is, and a rectangle cut out of a picture is the one presentation of that answer
// with the where removed.
//
// And all eight rather than the hits, which is what this showed first. A cell holding only
// what was found is a column of different lengths against its neighbours, so the eye cannot
// compare two parts without reading every tile — and a photograph that a part is NOT in
// still says something about the part, which a cell that omits it cannot say. Same
// pictures, same order, every column: what changes down the row is where the green box is.
//
// Nothing is cut here and nothing is encoded: a crop is a URL and a rectangle, the browser
// already has the photograph from the gallery above, and the box is four percentages drawn
// on top. The picture is the evidence; the box is the claim about it. Both, at every size.
//
// The zoom is where it gets looked at properly, in two steps: the photograph, and then the
// box within it. See ImageLightbox.

// FOUND, in one colour. The model used to grade its own answers high / medium / low and
// the tiles were ringed in three colours accordingly — but the grades did not survive
// being looked at: green boxes were wrong and amber ones were right, often enough that the
// colour was noise wearing the clothes of information. A box is a claim; whether it holds
// is settled by opening the picture, which is what the zoom is for.
const FOUND_RING = 'ring-blue-400/70';

// ONE COLOUR PER PART, THE SAME ON EVERY PICTURE — which is what makes a photograph with nine
// answers on it readable. The colour is the part; the labels name it once you are close
// enough to read them, and at thumbnail size the colour is all there is.
//
// Written out as values rather than Tailwind classes because they are chosen by index, and a
// class name built from an index is a class Tailwind never saw and therefore never emitted.
//
// Ten of them, in the app's own 500-weight family: saturated enough to sit on a white studio
// backdrop, far enough apart in hue to be told apart at 60 pixels. Past ten they repeat,
// which is honest — no palette tells fourteen colours apart on one chair, and the labels are
// what settle it there.
const TONES = [
  '#10b981', // emerald
  '#3b82f6', // blue
  '#f59e0b', // amber
  '#ec4899', // pink
  '#8b5cf6', // violet
  '#ef4444', // red
  '#06b6d4', // cyan
  '#84cc16', // lime
  '#f97316', // orange
  '#14b8a6', // teal
];

// BY THE PART'S PLACE AMONG THE PARTS BEING LOOKED FOR, not by its index in the manual's
// list: the list holds screws and tools this row never asks about, so indexing by it would
// spend the palette on parts that are never drawn and hand the two visible ones the same
// colour. A part keeps its place for as long as the parts list does, which is what makes the
// colour the same on all eight photographs and in the column beside them.
export const partTone = (nth: number): string => TONES[((nth % TONES.length) + TONES.length) % TONES.length];

// A PART'S COLOUR IN THE LISTING-CROPS ROW, on a photograph or on a page of the manual: its
// place among the parts the photograph call asks about, or — for the parts only the manual
// answers for — its place among those. One colour per part, whichever picture it is found on.
export const rowTone = (part: number, listing: number[], parts: number[]): string =>
  listing.includes(part)
    ? partTone(listing.indexOf(part))
    : partTone(parts.filter((p) => !listing.includes(p)).indexOf(part));

export function ListingCropCell({
  crops, photos, asked, waiting, pending, toneOf, rankOf, onZoom, alt, cropsOnly,
}: {
  crops: ListingCrop[];
  // Every photograph the run read, in the order it read them — the cell's own spine. The
  // crops are hung off it by URL rather than drawn in the order they arrived.
  photos: string[];
  // This part has been through a run. Which is what an empty cell needs to know: a part
  // found in none of the pictures and a part nobody has asked about would otherwise be the
  // same eight boxless photographs, and only one of them is an answer.
  asked: boolean;
  // STILL OUT: photographs the run in progress has not answered for yet. Given, the cell is
  // live — the tiles that have answered are drawn and these ones wait, one spinner each.
  //
  // Which is the difference between a row that reports and a row that stalls. The whole cell
  // used to be one spinner until the last of eight calls landed, on the sound argument that
  // the crops underneath belonged to the PREVIOUS run and would sit there looking settled.
  // That argument is about stale answers, and this is the same argument answered properly:
  // what is drawn is this run's, tile by tile, as it arrives.
  waiting?: Set<string>;
  // MORE MAY STILL ARRIVE, for a run that cannot name its pictures in advance. `waiting` is a
  // set of photographs because the listing half knows all eight before it sends the first call;
  // the page half is sent PER ASSEMBLY STEP and a step's crops land on whatever pages that step
  // happens to span, so until an answer comes back there is no tile to put a spinner on.
  //
  // So the spinner is the cell's own rather than a tile's: one more square at the end of the
  // grid, which keeps saying "still reading" while the tiles found so far appear beside it. An
  // empty cell during a run would otherwise read as the finished answer — this part is drawn
  // nowhere — which is the one thing it is not.
  pending?: boolean;
  // What colour to draw each crop in — its part's, wherever that part is drawn. A function
  // rather than one colour for the cell, because the cell that holds every part's crops at
  // once is exactly the one that needs them told apart.
  toneOf?: (crop: ListingCrop) => string | undefined;
  // WHICH OF THEM WERE KEPT, and in what order: the crop's rank in its part's selection,
  // written on its box. Undefined for a crop the selection passed over — it stays drawn, it
  // just carries no number.
  rankOf?: (crop: ListingCrop) => number | undefined;
  // The picture, and every instance of the part on it. An empty list still opens: the
  // photograph is worth looking at when the question is why the part is not in it.
  onZoom: (url: string, crops: ListingCrop[]) => void;
  alt: string;
  // THE CROPS AND NOT THE PICTURES THEY ARE IN — a piece of hardware on the manual's pages. A
  // screw is a few pixels of a whole step, so a tile of the step is a tile of everything else;
  // and the pages it is not on are not evidence of anything, the way a photograph it is
  // missing from is. So: one tile per crop, cut to its box, and a press still opens the whole
  // picture with every box on it.
  cropsOnly?: boolean;
}) {
  // THE PICTURES BEFORE ANY RUN, too: they are what a run will be asked about, and seeing them
  // is how somebody judges whether to press it. Not the crops-only cells (the manual's), whose
  // tiles are the answers and have no pictures to show before there is one.
  if (!asked && !crops.length && !waiting?.size && !pending && (cropsOnly || !photos.length)) return null;

  if (cropsOnly) {
    return (
      <div className="mx-auto flex max-w-[16rem] flex-wrap items-center justify-center gap-1">
        {crops.map((crop, k) => (
          <CropTile
            key={`${crop.url.length}-${k}-${crop.bbox.join(',')}`}
            crop={crop}
            label={alt}
            height={56}
            exact
            dim={crop.off}
            onOpen={() => onZoom(crop.url, crops.filter((c) => c.url === crop.url))}
          />
        ))}
        {pending && (
          <span className="flex h-14 w-14 items-center justify-center rounded bg-white ring-1 ring-slate-300">
            <Loader2 className="h-4 w-4 animate-spin text-slate-500" />
          </span>
        )}
      </div>
    );
  }

  // ONE TILE PER PHOTOGRAPH, with every instance boxed on it. A manual supplies six wood
  // buttons and a photograph of two chairs shows a dozen: those are one picture with a
  // dozen answers on it, not a dozen pictures. A tile each would also break the one thing
  // this row is arranged around — the same photographs in the same order in every cell,
  // which is what lets two parts be compared without reading every tile.
  //
  // Crops whose photograph is not in the list come last rather than disappearing: that is a
  // run read under another submodel's pictures, and a silently dropped answer is the
  // failure this phase has already paid for once.
  const tiles: { url: string; mine: ListingCrop[] }[] = [];
  const shown = new Set(photos);
  for (const url of photos) tiles.push({ url, mine: crops.filter((c) => c.url === url) });
  for (const url of new Set(crops.map((c) => c.url))) {
    if (!shown.has(url)) tiles.push({ url, mine: crops.filter((c) => c.url === url) });
  }

  return (
    // TWO ACROSS, which halves a row that was eight tiles tall and pushed everything under
    // it off the screen. A grid rather than a wrapping flex: wrapping decides how many fit
    // from each picture's own width, so a landscape shot and a portrait one in the same
    // listing gave two per line here and one there, and the tiles stopped lining up with
    // the part beside them — which is the whole reason every cell shows every photograph.
    // …and never wider than a pair of thumbnails. The tiles take their width from the
    // column, which is right in the parts-wide table where a column is a part — and wrong
    // on a single part's own tab, where that column is the whole table and each photograph
    // came out four hundred pixels across. A thumbnail is for finding the green box; the
    // zoom is for looking at it.
    <div className="mx-auto grid max-w-[16rem] grid-cols-2 items-center gap-1">
      {tiles.map(({ url, mine }) => {
        const out = !!waiting?.has(url);
        return (
        <button
          key={url}
          type="button"
          onClick={() => onZoom(url, mine)}
          // The count, when there is more than one: a tile with four rectangles on it at
          // this size is four smudges, and the number is the part of that answer legible
          // without opening it.
          title={mine.length > 1 ? `${alt} — ${mine.length}` : alt}
          aria-label={alt}
          // EVERY PICTURE AT FULL STRENGTH, and only the ring says which found something.
          //
          // A miss used to be dimmed, on the argument that it should not compete with the
          // hits beside it. But the pictures are the evidence and they are all equally
          // worth looking at — a photograph the part is NOT in is exactly the one you open
          // when you doubt the absence, and greying it made the row look like eight
          // pictures of which two had loaded.
          //
          // The green box is the answer anyway, and it is unmissable. The ring is there to
          // say the same thing about a tile too small to read, and to keep saying it when
          // every box on the picture has been struck through by hand.
          className={`relative block cursor-zoom-in overflow-hidden rounded bg-white ring-1 transition-opacity hover:opacity-90 ${
            mine.some((c) => !c.off) ? FOUND_RING : 'ring-slate-300'
          }`}
        >
          {/* The image sizes the button, so the button IS the picture and the box lands on
              the pixels — the same rule the zoom follows. The COLUMN gives the width and
              the picture's own proportions give the height: capping the height instead
              would leave a portrait shot narrower than its cell, and the box is drawn
              against the painted image, not against the box it sits in. */}
          <img
            src={url}
            alt=""
            loading="lazy"
            // Faded WHILE ITS CALL IS OUT, and only then. It is the one state where dimming
            // a picture says something true: this one has not been read yet. The rule above
            // — every picture at full strength — is about answers, and a tile with a spinner
            // on it is not one.
            className={`block h-auto w-full ${out ? 'opacity-40' : ''}`}
          />
          {out && (
            <span className="pointer-events-none absolute inset-0 flex items-center justify-center">
              <Loader2 className="h-4 w-4 animate-spin text-slate-500" />
            </span>
          )}
          {/* NO VEIL HERE, ever. The dim outside a rectangle is that rectangle's own
              shadow and does not compose, so it could only be drawn on tiles holding
              exactly one box — which made the same state look like two: six seats boxed
              alone came out darkened, two seats boxed together came out bright, decided by
              a number nobody is counting while scanning the column.

              A column read at a glance needs one appearance per state. The outline is that
              appearance; the veil belongs to the zoom, where one box on a full-screen
              photograph is exactly what it is for. */}
          {mine.map((crop, k) => (
            <BoxOverlay
              key={k}
              box={crop.bbox}
              // Traced, where the run traced it. At this size the polygon is most of what a
              // tile can say: two green rectangles on a chair are two rectangles, and the
              // shapes inside them are a leg and a rail.
              outlines={cropRings(crop)}
              tone={toneOf?.(crop)}
              label={rankOf?.(crop)?.toString()}
              labelInside
              rounded="rounded"
              off={crop.off}
              veil={false}
            />
          ))}
        </button>
        );
      })}
      {/* THE RUN IS NOT DONE. Across both columns while nothing has come back yet — a lone
          spinner in the left half of a cell reads as a picture that failed to load — and one
          square wide once there are tiles for it to follow. */}
      {pending && (
        <span
          className={`flex h-16 items-center justify-center rounded bg-white ring-1 ring-slate-300 ${
            tiles.length ? '' : 'col-span-2'
          }`}
        >
          <Loader2 className="h-4 w-4 animate-spin text-slate-500" />
        </span>
      )}
    </div>
  );
}
